'use client';

import { useTranslations } from 'next-intl';
import * as React from 'react';

interface ConversionProgressBarProps {
  /** Pages completed so far (the sidecar's assemble-stage count). */
  completed: number;
  /** Total pages, or 0 when the sidecar couldn't count them up front. */
  total: number;
}

/**
 * Live per-page progress for a high-effort (Docling) conversion. Renders a slim
 * determinate bar + "page X / N" while pages assemble. When `total` is unknown
 * (0) it degrades to "converting page N" with an indeterminate shimmer. Renders
 * nothing until the first page completes (avoids a 0-width flash on kickoff).
 */
export function ConversionProgressBar({ completed, total }: ConversionProgressBarProps) {
  const t = useTranslations('documents');
  if (completed <= 0) return null;

  const known = total > 0;
  const pct = known ? Math.min(100, Math.max(0, (completed / total) * 100)) : 100;

  return (
    <div className="mt-1 flex min-w-0 flex-col gap-1" aria-live="polite">
      <span className="text-[10px] font-medium text-muted-foreground">
        {known
          ? t('conversionProgressBar.pageProgress', { completed, total })
          : t('conversionProgressBar.convertingPage', { completed })}
      </span>
      <div
        className="h-1 w-full max-w-[160px] overflow-hidden rounded-full bg-accent/10"
        role="progressbar"
        aria-label={t('conversionProgressBar.progressAria')}
        aria-valuenow={known ? completed : undefined}
        aria-valuemin={0}
        aria-valuemax={known ? total : undefined}
      >
        <div
          data-testid="conversion-progress-fill"
          className={
            'h-full rounded-full bg-accent transition-[width] duration-500 ease-out' +
            (known ? '' : ' animate-pulse')
          }
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
